import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, Button, Divider, Icon } from 'investira.react.components';
import { BaseTemplate } from '../../components/templates';
import withMessage from '../../hoc/withMessage';

const AvaliacoesDocs = memo(() => {
  const navigate = useNavigate();
  const handleBackToTop = () => {
    const scrollContainer = document.getElementById('scroll');

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <BaseTemplate
      title={'Documentação'}
      subtitle={'Avaliações de Fundos e Gestores'}
      maxWidth={'md'}>
      <Stack spacing={2}>
        <Stack>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => navigate(-1)}
            sx={{ alignSelf: 'flex-start', mb: 2 }}
            startIcon={<Icon iconName="arrow_left" size={16} />}>
            Voltar
          </Button>
        </Stack>
        <Stack spacing={2}>
          <Typography variant="body1" color="textPrimary">
            Propomos considerar duas abordagens de avaliação: (i) análise quantitativa; e (ii)
            análise qualitativa. Os fundos receberão uma nota de classificação final composta pelos
            resultados parciais da <b>análise quantitativa (30%)</b> e da{' '}
            <b>análise qualitativa (70%)</b>.
          </Typography>

          <Typography variant="h6" color="textPrimary">
            1. ANÁLISE QUANTITATIVA
          </Typography>

          <Typography variant="body1" color="textPrimary">
            Esta análise é baseada em série histórica comparativa (36 meses) de dados públicos de
            fundos. A composição desses dados deve variar conforme as modalidades e características
            de gestão (ativa ou passiva). Portanto, trata-se de análise objetiva, passível de total
            automação.
          </Typography>

          <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 600 }}>
            PROPOSIÇÃO DE DADOS HISTÓRICOS
          </Typography>

          <Stack component="ol" spacing={1} sx={{ pl: 3, m: 0 }}>
            <Typography component="li" variant="body1" color="textPrimary">
              Para fundos passivos, cujo objetivo da gestão é perseguir (manter o mais próximo
              possível) um benchmark determinado, o Tracking Error (desvio padrão) é o dado
              analítico recomendado. Interpretação: quanto menor, melhor. Deve ser apresentado com
              sinal negativo.
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              Para fundos indexados, cujo objetivo da gestão é superar o retorno de um benchmark
              estabelecido para o fundo, sem grandes variações em relação a essa referência,
              considerar a composição de três dados analíticos:
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 3, mt: -0.5 }}>
              <Typography component="li" variant="body1" color="textPrimary">
                Tracking Error - 40% - quanto menor, melhor.
              </Typography>
              <Typography component="li" variant="body1" color="textPrimary">
                Retorno - 40% - quanto maior, melhor.
              </Typography>
              <Typography component="li" variant="body1" color="textPrimary">
                % de Vezes Acima do Benchmark - 20% - quanto maior, melhor.
              </Typography>
            </Stack>

            <Typography component="li" variant="body1" color="textPrimary">
              Para fundos ativos, cujo objetivo da gestão é obter maiores retornos sobre o
              benchmark, assumindo mais riscos, substituir o Tracking Error pelo Índice de Sharpe,
              com a seguinte composição:
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 3, mt: -0.5 }}>
              <Typography component="li" variant="body1" color="textPrimary">
                Índice de Sharpe - 40% - quanto maior, melhor.
              </Typography>
              <Typography component="li" variant="body1" color="textPrimary">
                Retorno - 40% - quanto maior, melhor.
              </Typography>
              <Typography component="li" variant="body1" color="textPrimary">
                % de Vezes Acima do Benchmark - 20% - quanto maior, melhor.
              </Typography>
            </Stack>

            <Typography component="li" variant="body1" color="textPrimary">
              Para fundos de risco e retorno, substituir o Tracking Error pelo Índice de Sharpe, com
              a seguinte composição:
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 3, mt: -0.5 }}>
              <Typography component="li" variant="body1" color="textPrimary">
                Índice de Sharpe - 60% - quanto maior, melhor.
              </Typography>
              <Typography component="li" variant="body1" color="textPrimary">
                Retorno - 20% - quanto maior, melhor.
              </Typography>
              <Typography component="li" variant="body1" color="textPrimary">
                % de Vezes Acima do Benchmark - 20% - quanto maior, melhor.
              </Typography>
            </Stack>
          </Stack>

          <Typography variant="body1" color="textPrimary">
            Após os resultados dos dados analíticos definidos nos itens anteriores, os fundos passam
            a ser ordenados de acordo com suas posições de resultado (ordem decrescente).
          </Typography>

          <Typography variant="body1" color="textPrimary">
            Para determinação do resultado da análise quantitativa comparativa, as notas entre 0 e 3
            serão atribuídas conforme a quantidade de fundos daquela classificação, aplicando-se a
            fórmula:
          </Typography>

          <Typography
            variant="body2"
            color="textPrimary"
            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {
              'Nota = ((posição do fundo avaliado - posição do último colocado) / (posição do último colocado - 1)) x (-3)'
            }
          </Typography>

          <Typography variant="body1" color="textPrimary">
            O ideal é que o sistema permita ao usuário estabelecer outros dados analíticos para
            série histórica, assim como alterações nas disposições de pesos.
          </Typography>

          <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 600 }}>
            ESCLARECIMENTOS ADICIONAIS
          </Typography>

          <Typography variant="body1" color="textPrimary">
            A análise quantitativa é realizada de forma comparativa, agrupando fundos de mesma
            modalidade. A ANBIMA fornece a classificação de fundos por modalidade. Contudo, há
            críticas quanto à sua validade, pois essa classificação decorre da declaração feita por
            cada fundo e, muitas vezes, não condiz com suas estratégias reais.
          </Typography>

          <Typography variant="body1" color="textPrimary">
            Alguns agentes de mercado e consultorias realizam classificações próprias com base na
            composição dos fundos, o que pode tornar o método mais confiável, desde que haja acesso
            às classificações desses agentes. Outra alternativa é usar os dados dos fundos
            disponíveis no sistema para manter uma classificação própria atualizada.
          </Typography>

          <Typography variant="body1" color="textPrimary">
            Há também duas outras questões importantes nesse processo de análise quantitativa. A
            primeira diz respeito a fundos mais recentes, sem histórico de 36 meses de dados
            analíticos. Nesse caso, o sistema deveria permitir a tabulação de períodos menores, como
            24 ou 12 meses, ou até mesmo a utilização de outro fundo do mesmo gestor, com a mesma
            modalidade e estratégia, comumente chamado de "espelho".
          </Typography>
        </Stack>
        <Divider />
        <Stack spacing={2}>
          <Typography variant="h6" color="textPrimary">
            2. ANÁLISE QUALITATIVA
          </Typography>

          <Typography variant="body1" color="textPrimary">
            Esta análise é subjetiva, baseada na aplicação de questionário de due diligence [
            <b>DDQ</b>], com o objetivo de avaliar: (i) a potencialidade fiduciária da gestora; (ii)
            o tratamento de conflitos e aspectos ASG; e (iii) as condições regulamentares do fundo.
            Com isso, é possível verificar a qualidade da gestão, tanto dos investimentos quanto dos
            riscos inerentes.
          </Typography>

          <Typography variant="body1" color="textPrimary">
            As respostas obtidas dos administradores/gestores dos fundos, de forma presencial ou por
            meio de questionários formais, são tabuladas para determinação de pontuações que
            resultarão em uma nota final variando de 0 a 3. O uso de I.A. é recomendável, pois pode
            reduzir a dependência, apenas, dos questionários.
          </Typography>

          <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 600 }}>
            PROPOSIÇÃO DE ROTEIRO DE QUESTIONÁRIO DE DUE DILIGENCE
          </Typography>

          <Typography variant="body1" color="textPrimary">
            {`a) Com relação à potencialidade fiduciária da gestora, as seguintes questões são
            avaliadas com relação às gestoras dos fundos:`}
          </Typography>

          <Stack component="ol" spacing={1} sx={{ pl: 6, m: 0 }}>
            <Typography component="li" variant="body1" color="textPrimary">
              classificação de qualidade de gestão [agências de rating];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              ativos sob gestão [AUM] consolidados - Brasil + Exterior;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              evolução do AUM consolidado no período avaliado;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              experiência em anos da equipe de gestão;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              experiência em anos da equipe de riscos;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              formação técnica da equipe de gestão;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              formação técnica da equipe de riscos;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              manutenção da equipe de gestão;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              manutenção da equipe de riscos;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              dependência de pessoa chave na gestão [S/N];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              tempo de dedicação dos membros da gestão ao fundo [no caso de FIP];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              uso de ferramentas / serviços especializados;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              consistência dos processos de gerenciamento de risco de crédito;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              consistência dos processos de gerenciamento de risco de mercado;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              consistência dos processos de gerenciamento de risco de liquidez;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              consistência dos processos de gerenciamento de risco operacional;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              consistência dos processos de gerenciamento de risco legal;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              regularidade na prestação de informações.
            </Typography>
          </Stack>

          <Typography variant="body1" color="textPrimary">
            {`b) Com relação a tratamento de conflitos e aspectos ASG, as seguintes questões são
            avaliadas com relação à gestora e, quando aplicável, às demais instituições prestadoras
            de serviços de administração e custódia do fundo:`}
          </Typography>

          <Stack component="ol" spacing={1} sx={{ pl: 6, m: 0 }}>
            <Typography component="li" variant="body1" color="textPrimary">
              gestora independente [S/N];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              skin in the game [se gestores investem no fundo e/ou suas estratégias];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              eficácia dos processos / área de compliance;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              tratamento dos investimentos de diretores e equipe de gestão;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              critérios de remuneração da equipe de riscos da gestora;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              nível de segregação de funções de gestão, administração e custódia;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              incorporação de aspectos ASG nos processos decisórios de investimentos;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              adesão a princípios de investimentos responsáveis;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              Adesão ao Código de Administração de Recursos de Terceiros da Anbima;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              cumprimento de background check regulatório da gestora;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              cumprimento de background check regulatório da administradora;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              cumprimento de background check regulatório da custodiante.
            </Typography>
          </Stack>

          <Typography variant="body1" color="textPrimary">
            {`c) Com relação às condições regulamentares dos fundos, as seguintes questões são
            avaliadas com base, exclusivamente, em regulamentos salvos no site da CVM:`}
          </Typography>

          <Stack component="ol" spacing={1} sx={{ pl: 6, m: 0 }}>
            <Typography component="li" variant="body1" color="textPrimary">
              previsão de obediência à norma CMN das EFPC [S/N];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              tipo de investidor [geral, qualificado];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              taxa máxima de administração;
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              uso de derivativos somente para proteção [S/N];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              vedação de crédito privado em fundos que não são de crédito [S/N];
            </Typography>
            <Typography component="li" variant="body1" color="textPrimary">
              restrição de alocação em ativos pertencentes ao grupo da gestora.
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack spacing={2} direction="row" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          color="primary"
          startIcon={<Icon iconName="arrow_positive" size={16} />}
          onClick={handleBackToTop}
          sx={{ alignSelf: 'flex-end' }}>
          Voltar ao topo
        </Button>
      </Stack>
    </BaseTemplate>
  );
});

AvaliacoesDocs.displayName = 'AvaliacoesDocs';

export default withMessage(AvaliacoesDocs);
